import {
  CommissionPayment,
  DashboardMetrics,
  Notification,
  Transaction,
  Offer,
} from '../types'

export const MOCK_METRICS: DashboardMetrics = {
  totalSales: 124500.0,
  commissionOwed: 18675.0,
  cashbackDistributed: 9337.5,
  uniqueUsers: 842,
  avgTransactionValue: 147.86,
}

// Helper to generate random location
const getRandomLocation = () => {
  const locs = ['loc_1', 'loc_2', 'loc_3', 'loc_4']
  return locs[Math.floor(Math.random() * locs.length)]
}

export const MOCK_TRANSACTIONS: Transaction[] = Array.from({ length: 100 })
  .map((_, i) => {
    const amount = Math.floor(Math.random() * 780) + 20
    const statusRoll = Math.random()
    let status: Transaction['status'] = 'paid'
    if (statusRoll > 0.95) status = 'failed'
    else if (statusRoll > 0.9) status = 'pending'
    else if (statusRoll > 0.8) status = 'processed'

    return {
      id: `txn_${1001 + i}`,
      date: new Date(
        Date.now() - Math.floor(Math.random() * 90) * 86400000,
      ).toISOString(),
      amount,
      commission: amount * 0.15,
      cashback: amount * 0.075,
      status,
      customerHash: `usr_${Math.floor(Math.random() * 50) + 1}`,
      locationId: getRandomLocation(),
    }
  })
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

// Generate daily sales data for charts
export const MOCK_CHART_DATA = Array.from({ length: 30 }).map((_, i) => {
  const date = new Date()
  date.setDate(date.getDate() - (29 - i))
  const sales = Math.floor(Math.random() * 5000) + 1000
  const commission = sales * 0.15
  const cashback = sales * 0.075
  const revenue = commission * 0.5

  return {
    date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    sales,
    commission,
    cashback,
    revenue,
    merchantCost: -commission, // For stacked bar chart visualization
  }
})

export const MOCK_PAYMENTS: CommissionPayment[] = [
  {
    id: 'inv_2024_001',
    periodStart: '2024-01-01',
    periodEnd: '2024-01-07',
    amount: 1250.0,
    status: 'paid',
    attemptCount: 1,
  },
  {
    id: 'inv_2024_002',
    periodStart: '2024-01-08',
    periodEnd: '2024-01-14',
    amount: 1450.0,
    status: 'paid',
    attemptCount: 1,
  },
  {
    id: 'inv_2024_003',
    periodStart: '2024-01-15',
    periodEnd: '2024-01-21',
    amount: 1100.0,
    status: 'failed',
    attemptCount: 2,
    nextRetryDate: '2024-01-25',
  },
  {
    id: 'inv_2024_004',
    periodStart: '2024-01-22',
    periodEnd: '2024-01-28',
    amount: 1600.0,
    status: 'processing',
    attemptCount: 1,
  },
  {
    id: 'inv_2024_005',
    periodStart: '2024-01-29',
    periodEnd: '2024-02-04',
    amount: 950.0,
    status: 'pending',
    attemptCount: 0,
  },
]

export const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: 'notif_1',
    title: 'Commission Charged Successfully',
    message: 'Commission of $1,250.00 for period Jan 1-7 has been processed.',
    type: 'success',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    read: false,
  },
  {
    id: 'notif_2',
    title: 'Payment Failed - Action Required',
    message:
      'Commission charge for Jan 15-21 failed. Please update your payment method.',
    type: 'error',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
    read: false,
  },
  {
    id: 'notif_3',
    title: 'Upcoming Charge Reminder',
    message: 'Your next commission charge is scheduled for Feb 5th.',
    type: 'info',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(), // 2 days ago
    read: true,
  },
]

export const MOCK_OFFERS: Offer[] = [
  {
    id: 'off_1',
    name: 'Standard Cashback',
    description: '8% cashback on all purchases for loyal customers.',
    type: 'fixed',
    status: 'active',
    cashbackPercentage: 8,
    locations: ['loc_1', 'loc_2', 'loc_3', 'loc_4', 'loc_5'],
    createdAt: '2023-12-01T10:00:00Z',
  },
  {
    id: 'off_2',
    name: 'Volume Rewards',
    description: 'Earn more when you spend more. Up to 10% cashback.',
    type: 'tiered',
    status: 'active',
    tiers: [
      { minAmount: 0, maxAmount: 50, cashbackPercentage: 5 },
      { minAmount: 51, maxAmount: 150, cashbackPercentage: 7 },
      { minAmount: 151, maxAmount: null, cashbackPercentage: 10 },
    ],
    locations: ['loc_1', 'loc_2', 'loc_3'],
    createdAt: '2024-01-10T14:30:00Z',
  },
  {
    id: 'off_3',
    name: 'Winter Special',
    description: 'Limited time offer for the winter season.',
    type: 'promotional',
    status: 'scheduled',
    standardPercentage: 5,
    promotionalPercentage: 15,
    startDate: '2024-02-15',
    endDate: '2024-02-28',
    locations: ['loc_1'],
    createdAt: '2024-01-25T09:15:00Z',
  },
  {
    id: 'off_4',
    name: 'Service Boost',
    description: 'Higher cashback on installation services.',
    type: 'category',
    status: 'active',
    categories: [
      { name: 'Installation', cashbackPercentage: 8 },
      { name: 'Products', cashbackPercentage: 5 },
      { name: 'Support', cashbackPercentage: 10 },
    ],
    locations: ['loc_1', 'loc_2'],
    createdAt: '2024-01-05T11:20:00Z',
  },
  {
    id: 'off_5',
    name: 'New Customer Offer',
    description: 'Special rate for first-time visitors.',
    type: 'fixed',
    status: 'inactive',
    cashbackPercentage: 12,
    locations: [],
    createdAt: '2024-01-28T16:45:00Z',
  },
  {
    id: 'off_6',
    name: 'Holiday Sale',
    description: 'End of year special.',
    type: 'promotional',
    status: 'expired',
    standardPercentage: 5,
    promotionalPercentage: 20,
    startDate: '2023-12-01',
    endDate: '2023-12-31',
    locations: ['loc_1', 'loc_2', 'loc_3', 'loc_4'],
    createdAt: '2023-11-15T10:00:00Z',
  },
]
